import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { FolderOpen } from 'lucide-react';
import { computeVisualBounds } from '../../utils/imageUtils';
import styled, { keyframes } from 'styled-components';

import { useDesignContext } from '../../hooks/useDesignContext';
import { buildApiUrl } from '../../config/api';

// Module-level image memory cache.
// Keeping HTMLImageElement references alive forces the browser to retain those
// images in its memory cache for the lifetime of the JS session.  When an
// <img> element remounts with the same src, the browser serves it instantly
// from memory — zero additional network requests.
const imageMemoryCache = new Map<string, HTMLImageElement>();
function preloadToMemory(src: string): void {
  if (!src || imageMemoryCache.has(src)) return;
  const img = new Image();
  // crossOrigin must be set BEFORE src so the browser fetches with an Origin
  // header.  The AssetController already responds with Access-Control-Allow-Origin:*,
  // so the response will be CORS-safe and canvas.getImageData() won't taint.
  img.crossOrigin = 'anonymous';
  img.src = src;
  imageMemoryCache.set(src, img);
}

// ---------------------------------------------------------------------------
// Module-level API response caches — these survive panel close/reopen so that
// reopening the Art panel NEVER triggers duplicate network requests.
// Keys are URL query-strings (or JSON-serialised filter objects for artworks).
// ---------------------------------------------------------------------------
const moduleArtworksCache = new Map<string, any[]>();   // artwork lists
const modulePlacementsCache = new Map<string, any[]>();   // available placements
const moduleAllCatCache = new Map<string, any[]>();   // all-categories list
const moduleInnerArtCache = new Map<string, any[]>();   // inner artworks (e.g. SL12 options)

// ---------------------------------------------------------------------------
// Module-level navigation session — restores the last-visited state when the
// panel is reopened so <img> elements are never recreated from scratch.
// ---------------------------------------------------------------------------
const moduleSession: {
  granite: string | null;
  modelId: number | null;
  sizeId: number | null;
  placement: string | null;
  category: string | null;
  artworks: any[];
} = {
  granite: null, modelId: null, sizeId: null, placement: null, category: null, artworks: [],
};

// Constants
const GRANITES = [
  { id: 'PJB', label: 'Premium Jet Black' },
  { id: 'GREY', label: 'Medium Grey' }
];
const PLACEMENTS = [
  { id: 'corner', label: 'Corner' },
  { id: 'left', label: 'Left' },
  { id: 'top', label: 'Top' },
  { id: 'special', label: 'Special' }
];

// Module-level label helpers — GRANITES/PLACEMENTS are constants so these
// never need to be recreated inside the component as useCallback.
const getGraniteLabel = (id: string | null) => GRANITES.find(g => g.id === id)?.label || '';
const getPlacementLabel = (id: string | null) => PLACEMENTS.find(p => p.id === id)?.label || '';
const ART_PER_PAGE = 60; // 60 designs per page

// Styled Components
const ArtGrid = styled.div<{ $columns?: string }>`
  display: grid;
  grid-template-columns: ${props => props.$columns || 'repeat(auto-fill, minmax(95px, 1fr))'};
  gap: ${props => props.$columns === '1fr' ? '0' : '10px'};
  width: 100%;
  padding: ${props => props.$columns === '1fr' ? '0' : '0 16px'};
  box-sizing: border-box;
`;

const ArtCard = styled.button<{ $disabled?: boolean; $isLoading?: boolean; $aspectRatio?: string; $isFullWidth?: boolean }>`
  background: #fff;
  transition: box-shadow 0.12s, border-color 0.12s, transform 0.12s;
  text-align: left;
  font-family: 'Inter', 'Segoe UI', Arial, sans-serif;
  font-size: 11px;
  padding: 5px;
  height: fit-content;
  position: relative;
  ${props => props.$isFullWidth ? 'border: none; border-radius: 0; margin-bottom: 8px;' : 'border: 1px solid #e1e5e9; border-radius: 8px;'}
  ${props => props.$aspectRatio ? `aspect-ratio: ${props.$aspectRatio};` : !props.$isFullWidth ? 'aspect-ratio: 1/1.2;' : ''}
  width: 100%;
  min-width: 0;
  opacity: ${props => (props.$disabled || props.$isLoading) ? 0.6 : 1};
  cursor: ${props => (props.$disabled || props.$isLoading) ? 'not-allowed' : 'pointer'};
`;

const Thumb = styled.div<{ $fitContent?: boolean; $bgColor?: string }>`
  width: 100%;
  ${props => props.$fitContent ? 'height: 110px;' : 'height: 100%;'}
  background: ${props => props.$bgColor ?? (props.$fitContent ? '#f8fafc' : '#e6eaf0')};
  border-radius: ${props => props.$fitContent ? '4px' : '6px'};
  overflow: hidden;
  margin-bottom: ${props => props.$fitContent ? '4px' : '6px'};
  position: relative;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    background: ${props => props.$bgColor ?? '#000000'};
    position: absolute;
    left: 0; 
    top: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }
`;

const Caption = styled.div`
  font-size: 10px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: center;
  color: #22334d;
  font-family: 'Inter', 'Segoe UI', Arial, sans-serif;
`;

const FormGroup = styled.div`
  margin-bottom: 12px;
  font-family: 'Inter', 'Segoe UI', Arial, sans-serif;
  display: flex;
  padding: 5px;
  flex-direction: column;
  height: 100%;
  min-width: 0;
  min-height: 0;
`;

const ScrollableContent = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  scrollbar-width: none; /* Hide scrollbars for Firefox */
  &::-webkit-scrollbar {
    display: none; /* Hide scrollbars for Chrome/Safari/Opera */
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 7px 10px 7px 28px;
  border: 1.5px solid #e1e5e9;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 400;
  background: #f7f9fa;
  font-family: 'Inter', 'Segoe UI', Arial, sans-serif;
  
  &:focus {
    outline: none;
    border-color: #5D722C;
    background: #fff;
  }
  
  &::placeholder {
    color: #b0b7c3;
    font-weight: 400;
  }
`;

const FolderGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 8px;
  margin-bottom: 8px;
`;

const SectionTitle = styled.div`
  font-weight: 700;
  margin-bottom: 8px;
  font-size: 13px;
  letter-spacing: 0.3px;
  color: #0f172a;
`;

const FolderCard = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  cursor: pointer;
  padding: 8px 6px;
  box-shadow: 0 1px 2px rgba(2, 6, 23, 0.06);
  transition: border-color 0.12s, box-shadow 0.12s, transform 0.10s, background 0.12s;
  min-height: 0;
  height: fit-content;
  position: relative;
  font-family: 'Inter', 'Segoe UI', Arial, sans-serif;
  font-size: 12px;
  user-select: none;
  
  &:hover {
    border-color: rgba(93, 114, 44, 0.55);
    box-shadow: 0 6px 16px rgba(2, 6, 23, 0.10);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0px);
  }

  &:focus-visible {
    outline: none;
    border-color: #5D722C;
    box-shadow: 0 0 0 3px rgba(93, 114, 44, 0.22);
  }
`;

const FolderIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 14px;
  margin-bottom: 8px;
  background: rgba(93, 114, 44, 0.12);
  border: 1px solid rgba(93, 114, 44, 0.20);
  color: #5D722C;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FolderLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: #22334d;
  text-align: center;
  white-space: pre-line;
  word-break: break-word;
  line-height: 1.2;
  font-family: 'Inter', 'Segoe UI', Arial, sans-serif;
`;

const FolderMeta = styled.div`
  margin-top: 2px;
  font-size: 10px;
  color: #64748b;
  text-align: center;
  line-height: 1.2;
`;

const PlacementGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 10px;
`;

const PlacementCard = styled.button`
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px 16px;
  cursor: pointer;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Inter', 'Segoe UI', Arial, sans-serif;
  font-weight: 700;
  font-size: 14px;
  color: #1e293b;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
  letter-spacing: 0.3px;
  position: relative;
  overflow: hidden;
  
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(93, 114, 44, 0.03) 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover {
    border-color: #5D722C;
    background: #ffffff;
    box-shadow: 0 10px 15px -3px rgba(93, 114, 44, 0.15), 0 4px 6px -2px rgba(93, 114, 44, 0.1);
    transform: translateY(-2px);
    color: #5D722C;
    
    &::after {
      opacity: 1;
    }
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px -1px rgba(93, 114, 44, 0.1);
  }
`;

const Breadcrumbs = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  padding: 12px 16px 8px;
  color: #22334d;
  font-family: 'Inter', 'Segoe UI', Arial, sans-serif;
  flex-wrap: wrap;
  user-select: none;
  
  .crumb {
    cursor: pointer;
    color: #5D722C;
    font-weight: 500;
    
    &:hover { 
      text-decoration: underline; 
    }
  }
  
  .sep {
    color: #b0b7c3;
    margin: 0 2px;
  }
  
  .current {
    color: #22334d;
    font-weight: 600;
    cursor: default;
  }
`;


const StepContainer = styled.div<{ $isVisible: boolean }>`
  display: ${props => props.$isVisible ? 'flex' : 'none'};
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

interface ArtItemProps {
  artwork: any;
  status: 'loading' | 'loaded' | 'error';
  primary: string;
  isTemplateSelected: boolean;
  selectedPlacement: string | null;
  graniteColor?: string | null;
  onSelect: (artwork: any) => void;
  onLoad: (id: string) => void;
  onError: (id: string) => void;
}

const ArtItem = React.memo(({ artwork, status, primary, isTemplateSelected, selectedPlacement, graniteColor, onSelect, onLoad, onError }: ArtItemProps) => {
  const isTopOrSpecial = selectedPlacement === 'top' || selectedPlacement === 'special';
  const isPJB = graniteColor ? String(graniteColor).toUpperCase().includes('PJB') || String(graniteColor).toUpperCase().includes('BLACK') : false;
  const thumbBg = isPJB ? '#111111' : '#e1e1e1';
  return (
    <ArtCard
      onClick={() => status !== 'loading' && onSelect(artwork)}
      title={status === 'loading' ? 'Loading artwork...' : `${artwork.art_code} - ${artwork.title}`}
      $disabled={!isTemplateSelected}
      $isLoading={status === 'loading'}
      $aspectRatio={isTopOrSpecial ? 'auto' : undefined}
      $isFullWidth={isTopOrSpecial}
    >
      <Thumb $fitContent={isTopOrSpecial} $bgColor={thumbBg}>
        {primary && status !== 'error' ? (
          <>
            <img
              src={primary}
              crossOrigin="anonymous"
              loading="lazy"
              alt={artwork.art_code || artwork.title}
              onLoad={() => onLoad(artwork.id)}
              onError={() => onError(artwork.id)}
            />
            {status === 'loading' && (
              <SpinnerOverlay>
                <LocalSpinner $size={20} />
              </SpinnerOverlay>
            )}
          </>
        ) : (
          <div style={{
            width: '100%',
            height: '100%',
            background: '#e6eaf0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#b0b7c3',
            fontSize: '9px'
          }}>
            No Image
          </div>
        )}
      </Thumb>
      <Caption>{artwork.art_code}</Caption>
      <div style={{ fontSize: 9, color: '#5D722C', fontWeight: 600, textAlign: 'center', marginTop: 2 }}>
        {artwork.category_name || artwork.category || ''}
      </div>
      <div style={{ fontSize: 8, color: '#94a3b8', textAlign: 'center', marginTop: 1 }}>
        {artwork.type}
      </div>
    </ArtCard>
  );
});
ArtItem.displayName = 'ArtItem';


// Use centralized Preloader for image loading indicators

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const LocalSpinner = styled.div<{ $size?: number }>`
  width: ${props => props.$size || 24}px;
  height: ${props => props.$size || 24}px;
  border: 3px solid rgba(93, 114, 44, 0.2);
  border-top-color: #5D722C;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const SpinnerOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.6);
  z-index: 10;
`;

const CenterLoader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  width: 100%;
  color: #5D722C;
  font-size: 13px;
  font-weight: 500;
  gap: 12px;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: 7px 10px;
  border: 1.5px solid ${props => props.$variant === 'primary' ? '#4a5b23' : '#b8cc8a'};
  border-radius: 6px;
  background: ${props => props.$variant === 'primary' ? '#5D722C' : '#eaf4d6'};
  color: ${props => props.$variant === 'primary' ? '#fff' : '#2f4a1c'};
  cursor: pointer;
  font-weight: 500;
  font-size: 12px;
  font-family: 'Inter', 'Segoe UI', Arial, sans-serif;
  transition: all 0.12s;
  
  &:hover:not(:disabled) {
    border-color: ${props => props.$variant === 'primary' ? '#4a5b23' : '#9fbc69'};
    ${props => props.$variant === 'primary' ? 'background: #4a5b23;' : 'background: #dcebbd; box-shadow: 0 1px 4px rgba(93, 114, 44, 0.16);'}
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;



// ---------------------------------------------------------------------------
// URL helpers
// ---------------------------------------------------------------------------

/** Strip any server-side path prefix and return a clean relative path. */
const normaliseUploadPath = (raw: string): string => {
  let path = raw.replace(/\\/g, '/');
  const publicIdx = path.toLowerCase().lastIndexOf('public/uploads/');
  if (publicIdx >= 0) return path.slice(publicIdx + 'public/uploads/'.length).replace(/^\/+/, '');
  const uploadsIdx = path.toLowerCase().lastIndexOf('/uploads/');
  if (uploadsIdx >= 0) return path.slice(uploadsIdx + '/uploads/'.length).replace(/^\/+/, '');
  if (path.toLowerCase().startsWith('uploads/')) return path.slice('uploads/'.length);
  return path.replace(/^\/+/, '');
};

/** Resolve an artwork's thumbnail URL (thumb fields first, then image fields). */
const resolveArtworkThumbUrls = (artwork: any, buildApiUrlFn: (path: string) => string): { primary: string } => {
  const raw = artwork?.thumb_url || artwork?.thumb_path || artwork?.image_url || artwork?.image_path
    || artwork?.file_url || artwork?.file_path || artwork?.src || artwork?.url;
  if (!raw) return { primary: '' };
  const s = String(raw);
  if (/^https?:\/\//i.test(s) || /^data:/i.test(s)) return { primary: s };
  return { primary: buildApiUrlFn(`/api/uploads/${normaliseUploadPath(s)}`) };
};

/** Resolve the full-resolution URL for an artwork (original fields first, then thumb). */
const resolveArtworkOriginalUrl = (artwork: any, buildApiUrlFn: (path: string) => string): string => {
  const raw = artwork?.image_url || artwork?.image_path || artwork?.file_url || artwork?.file_path
    || artwork?.original_url || artwork?.src || artwork?.url || artwork?.path
    || artwork?.thumb_url || artwork?.thumb_path;
  if (!raw) return '';
  const s = String(raw);
  if (/^https?:\/\//i.test(s) || /^data:/i.test(s)) return s;
  return buildApiUrlFn(`/api/uploads/${normaliseUploadPath(s)}`);
};

// Types
interface ArtProps {
  onClose: () => void;
  onArtworkSelect: (artwork: any) => void;
  setImageGridActive?: (active: boolean) => void;
  forcedGranite?: string | null;
}

interface BreadcrumbItem {
  label: string;
  onClick?: () => void;
  isCurrent?: boolean;
}

// Main Component
const Art: React.FC<ArtProps> = ({
  onClose,
  onArtworkSelect,
  setImageGridActive,
  forcedGranite,
}) => {
  const {
    currentDesign,
    updateDesign,
    isTemplateSelected,
    selectedElement,
    setSelectedElement,
    layoutGuides,
    activeSide,
    placementSlots,
    activePlacementSlot,
    setActivePlacementSlot,
    pixelsPerInch,
    renderedTemplateOrigin,
    sharedCategories,
  } = useDesignContext();

  const isLibraryMode = !isTemplateSelected;

  const selectedModelId = (currentDesign as any)?.selectedModelId ? Number((currentDesign as any).selectedModelId) : null;
  const selectedSizeId = (currentDesign as any)?.selectedSizeId ? Number((currentDesign as any).selectedSizeId) : null;

  // Stable refs so callbacks don't change identity when model/size IDs update
  const selectedModelIdRef = useRef<number | null>(selectedModelId);
  const selectedSizeIdRef = useRef<number | null>(selectedSizeId);
  selectedModelIdRef.current = selectedModelId;
  selectedSizeIdRef.current = selectedSizeId;

  // Skip the forcedGranite effect on initial mount — state is already initialized
  // from the prop; running it on mount only resets dedup keys that other effects
  // just set, causing duplicate requests.
  const forcedGraniteInitRef = useRef(true);

  // Deduplication keys — skip fetches when params haven't changed
  const lastCategoryFetchKeyRef = useRef<string>('__INIT__');
  const lastPlacementFetchKeyRef = useRef<string>('__INIT__');
  const lastArtworkFetchKeyRef = useRef<string>('__INIT__');

  // Stable ref for selectedGranite so callbacks don't need it in deps
  const selectedGraniteRef = useRef<string | null>(null);

  // Determine initial granite and session match state before initialising state
  const initialGranite = forcedGranite ?? (isLibraryMode ? null : 'PJB');
  const sessionMatches = moduleSession.granite === initialGranite
    && moduleSession.modelId === selectedModelId
    && moduleSession.sizeId === selectedSizeId;

  // ---------------------------------------------------------------------------
  // State — initialised from moduleSession so reopening the panel restores
  // the previous navigation state and never recreates <img> elements.
  // ---------------------------------------------------------------------------
  const [artworks, setArtworks] = useState<any[]>(() => sessionMatches ? moduleSession.artworks : []);
  const [artworkCategories, setArtworkCategories] = useState<any[]>([]);
  const [artLoading, setArtLoading] = useState<boolean>(false);
  const [artError, setArtError] = useState<string | null>(null);
  // track per-image load status: 'loading' | 'loaded' | 'error'
  const [imageStatus, setImageStatus] = useState<Record<string, 'loading' | 'loaded' | 'error'>>({});
  // Cache for loaded images to prevent spinner flash on revisit
  const loadedImagesRef = useRef<Set<string | number>>(new Set());
  const [artQuery, setArtQuery] = useState<string>('');
  const [debouncedArtQuery, setDebouncedArtQuery] = useState<string>('');
  const [artPage, setArtPage] = useState<number>(1);
  const [selectingArt, setSelectingArt] = useState<boolean>(false);
  // In template mode the granite is always known from the stone colour —
  // default to forcedGranite when set, otherwise 'PJB' so the user goes
  // straight to Placement → Category → Images without a granite step.
  // Library mode keeps the original behaviour (starts at null → user picks).
  const [selectedGranite, setSelectedGranite] = useState<string | null>(initialGranite);
  // Keep selectedGraniteRef in sync so placement/category fetches read the latest value
  selectedGraniteRef.current = selectedGranite;

  const [selectedPlacement, setSelectedPlacement] = useState<string | null>(() => {
    if (activePlacementSlot) {
      const pType = activePlacementSlot.slot_type;
      if (pType === 'corner_left' || pType === 'corner_right') return 'corner';
      if (pType === 'porcelain') return 'special';
      return pType;
    }
    return sessionMatches ? moduleSession.placement : null;
  });

  // Restore category only if the session granite matches (placement will also match)
  const [selectedCategory, setSelectedCategory] = useState<string | null>(
    sessionMatches ? moduleSession.category : null
  );

  // folderCategories is derived from the fetched artworks so counts are
  // always accurate per-placement (the API /categories endpoint returns global
  // counts and cannot be filtered reliably by placement).
  // This replaces the old setFolderCategories / fetchFolderCategories approach.
  const [availablePlacements, setAvailablePlacements] = useState<any[]>([]);
  const [placementImageStatus, setPlacementImageStatus] = useState<Record<string, 'loading' | 'loaded' | 'error'>>({});
  const [placementsLoading, setPlacementsLoading] = useState<boolean>(false);
  // categoriesLoading is no longer a separate state — the category step is shown
  // once artLoading is false and artworks are populated (folderCategories derived from them).

  // Sync categories from shared context
  useEffect(() => {
    if (sharedCategories.length > 0 && !selectedGranite && !selectedModelId) {
      setArtworkCategories(sharedCategories);
    }
  }, [sharedCategories, selectedGranite, selectedModelId]);
  const [innerArtworks, setInnerArtworks] = useState<any[]>([]);
  const [innerLoading, setInnerLoading] = useState<boolean>(false);
  const [showInnerSelection, setShowInnerSelection] = useState(false);
  const [currentParentArt, setCurrentParentArt] = useState<any | null>(null);
  const [userDismissedInnerSelection, setUserDismissedInnerSelection] = useState(false);

  // Custom artwork upload (temporary, not stored in database)
  const customArtworkInputRef = useRef<HTMLInputElement | null>(null);
  // Memoized values
  const filteredPlacements = useMemo(() =>
    PLACEMENTS.filter(p => availablePlacements.includes(p.id))
    , [availablePlacements]);

  // Are all placement thumbnails finished loading (loaded or error)
  const allPlacementImagesLoaded = useMemo(() => {
    if (isLibraryMode) return true;
    if (!filteredPlacements || filteredPlacements.length === 0) return true;
    return filteredPlacements.every(p => {
      const s = placementImageStatus[p.id];
      return s === 'loaded' || s === 'error';
    });
  }, [filteredPlacements, placementImageStatus, isLibraryMode]);

  // Derive per-placement category list directly from the fetched artworks.
  // Counts are always accurate because they reflect what the API actually returned
  // for this placement — unlike the /categories endpoint which returns global totals.
  const folderCategories = useMemo(() => {
    if (!artworks.length || !artworkCategories.length) return [];
    const countMap = new Map<number, number>();
    artworks.forEach((a: any) => {
      const id = Number(a.category_id);
      if (id) countMap.set(id, (countMap.get(id) || 0) + 1);
    });
    return artworkCategories
      .filter((c: any) => countMap.has(Number(c.id)))
      .map((c: any) => ({ ...c, artwork_count: countMap.get(Number(c.id)) || 0 }))
      .sort((a: any, b: any) => String(a.name || '').localeCompare(String(b.name || '')));
  }, [artworks, artworkCategories]);

  // Client-side category filter — artworks is always the FULL placement set.
  // Filtering here (not via API) means category switches never trigger new
  // requests and never unmount <img> elements.
  const filteredArtworks = useMemo(() => {
    if (!selectedCategory || selectedCategory === 'All') return artworks;
    const catName = selectedCategory.toLowerCase();
    // Look up numeric ID from folderCategories (accurate per-placement list)
    const catId = (() => {
      const found = folderCategories.find((c: any) => String(c?.name || '').toLowerCase() === catName);
      const n = found?.id != null ? Number(found.id) : NaN;
      return Number.isFinite(n) && n > 0 ? n : null;
    })();

    return artworks.filter((a: any) => {
      // Match by numeric category_id (most reliable)
      if (catId && Number(a.category_id) === catId) return true;
      // Fallback: match by string fields
      const aName = (a.category_name || a.category || '').toLowerCase();
      return aName === catName;
    });
  }, [artworks, selectedCategory, folderCategories]);

  const totalPages = useMemo(() =>
    Math.max(1, Math.ceil(filteredArtworks.length / ART_PER_PAGE))
    , [filteredArtworks.length]);

  const safePage = useMemo(() =>
    Math.min(artPage, totalPages)
    , [artPage, totalPages]);

  const pageItems = useMemo(() =>
    filteredArtworks.slice((safePage - 1) * ART_PER_PAGE, safePage * ART_PER_PAGE)
    , [filteredArtworks, safePage]);

  // Set of IDs on the current visible page — used by the grid to CSS-hide
  // all other items without unmounting them.
  const pageItemIdSet = useMemo(() =>
    new Set(pageItems.map((a: any) => a.id))
    , [pageItems]);

  const isPreloading = placementsLoading || artLoading;

  // API Calls
  const fetchArtworks = useCallback(async (filters: any) => {
    const cacheKey = JSON.stringify(filters);

    // Serve from module-level cache — survives panel close/reopen
    if (moduleArtworksCache.has(cacheKey)) {
      setArtworks(moduleArtworksCache.get(cacheKey)!);
      return;
    }

    try {
      setArtLoading(true);
      setArtError(null);

      const params = new URLSearchParams();
      if (filters.granite_type) params.append('granite_type', filters.granite_type);
      if (filters.placement) params.append('placement', filters.placement);
      if (filters.category_id) params.append('category_id', filters.category_id);
      if (filters.category_name) params.append('category_name', filters.category_name);
      if (filters.search) params.append('search', filters.search);
      if (filters.model_id) params.append('model_id', String(filters.model_id));
      if (filters.size_id) params.append('size_id', String(filters.size_id));

      const url = buildApiUrl('/api/art') + (params.toString() ? `?${params.toString()}` : '');
      const res = await fetch(url);

      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      const data = await res.json();

      const raw = (data.status && data.data) ? data.data : [];
      const activeOnly = Array.isArray(raw)
        ? raw.filter((a: any) => (typeof a?.is_active === 'undefined' || a?.is_active === null || Number(a.is_active) === 1))
        : [];

      moduleArtworksCache.set(cacheKey, activeOnly); // persist across panel open/close

      // Preload thumbnails: first page items immediately, the rest after a short
      // delay so we don't saturate the browser's connection pool while the page
      // is also loading other critical assets.
      const firstPage = activeOnly.slice(0, ART_PER_PAGE);
      const rest = activeOnly.slice(ART_PER_PAGE);
      firstPage.forEach((a: any) => {
        const { primary } = resolveArtworkThumbUrls(a, buildApiUrl);
        preloadToMemory(primary);
      });
      if (rest.length) {
        setTimeout(() => {
          rest.forEach((a: any) => {
            const { primary } = resolveArtworkThumbUrls(a, buildApiUrl);
            preloadToMemory(primary);
          });
        }, 800); // defer background preload by 800 ms
      }

      setArtworks(activeOnly);
    } catch (e: any) {
      setArtError(e.message || 'Unable to fetch artwork');
      setArtworks([]);
      setImageStatus({});
    } finally {
      setArtLoading(false);
    }
  }, []);

  const fetchPlacements = useCallback(async (graniteType: string) => {
    const modelId = selectedModelIdRef.current;
    const sizeId = selectedSizeIdRef.current;

    const params = new URLSearchParams();
    params.set('granite_type', graniteType);
    if (modelId) params.set('model_id', String(modelId));
    if (sizeId) params.set('size_id', String(sizeId));

    const key = params.toString();
    if (lastPlacementFetchKeyRef.current === key) return; // skip duplicate
    lastPlacementFetchKeyRef.current = key;

    // Serve from module-level cache — survives panel close/reopen
    if (modulePlacementsCache.has(key)) {
      setAvailablePlacements(modulePlacementsCache.get(key)!);
      return;
    }

    setPlacementsLoading(true);
    try {
      const url = buildApiUrl('/api/art/placements') + `?${key}`;
      const res = await fetch(url);

      if (res.ok) {
        const data = await res.json();
        const list = data.status && data.data ? data.data : [];
        modulePlacementsCache.set(key, list);
        setAvailablePlacements(list);
      } else {
        setAvailablePlacements([]);
      }
    } catch (e) {
      setAvailablePlacements([]);
    } finally {
      setPlacementsLoading(false);
    }
  }, []); // stable — reads model/size from refs

  const fetchInnerArtworks = useCallback(async (artCode: string) => {
    // Serve from module-level cache — no re-fetch on repeated SL12 clicks
    if (moduleInnerArtCache.has(artCode)) {
      setInnerArtworks(moduleInnerArtCache.get(artCode)!);
      return;
    }
    setInnerLoading(true);
    try {
      const url = buildApiUrl(`/api/art/inner?art_code=${encodeURIComponent(artCode)}&model_id=${selectedModelIdRef.current || ''}&size_id=${selectedSizeIdRef.current || ''}`);
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        const list = data.status && data.data ? data.data : [];
        moduleInnerArtCache.set(artCode, list);
        setInnerArtworks(list);
      }
    } catch (e) {
    } finally {
      setInnerLoading(false);
    }
  }, []); // stable — buildApiUrl is a module-level import, never changes

  // Re-trigger inner artwork selection when SL12 is clicked in the designer
  useEffect(() => {
    if (selectedElement && isTemplateSelected) {
      const el = currentDesign.elements.find(e => e.id === selectedElement);
      if (!el) return;

      const isSL12 = el.properties?.isSL12;
      const isInner = el.properties?.isInnerArtwork;

      if (isSL12 || isInner) {
        // Don't auto-reopen if the user explicitly dismissed the inner selection via Back
        if (userDismissedInnerSelection) return;

        const parentId = isInner ? el.properties?.parentId : el.id;
        const parentEl = currentDesign.elements.find(e => e.id === parentId);

        if (parentEl && (currentParentArt?.art_code !== 'SL12' || !showInnerSelection)) {
          setCurrentParentArt({
            art_code: 'SL12',
            title: parentEl.properties?.alt || 'SL12',
            thumb_url: parentEl.properties?.src
          });
          setShowInnerSelection(true);
          setUserDismissedInnerSelection(false);
          fetchInnerArtworks('SL12');
        }
      } else {
        // Selected something else, reset dismissal flag so clicking SL12 again will work
        setUserDismissedInnerSelection(false);
      }
    }
  }, [selectedElement, isTemplateSelected, currentDesign.elements, currentParentArt, fetchInnerArtworks, showInnerSelection, userDismissedInnerSelection]);



  const fetchAllCategories = useCallback(async (graniteType?: string) => {
    const modelId = selectedModelIdRef.current;
    const sizeId = selectedSizeIdRef.current;

    const params = new URLSearchParams();
    if (graniteType) params.append('granite_type', graniteType);
    if (modelId) params.append('model_id', String(modelId));
    if (sizeId) params.append('size_id', String(sizeId));

    const key = params.toString();
    if (lastCategoryFetchKeyRef.current === key) return; // skip duplicate
    lastCategoryFetchKeyRef.current = key;

    // Serve from module-level cache — survives panel close/reopen
    if (moduleAllCatCache.has(key)) {
      setArtworkCategories(moduleAllCatCache.get(key)!);
      return;
    }

    try {
      const url = buildApiUrl('/api/art/categories') + (key ? `?${key}` : '');
      const res = await fetch(url);

      if (res.ok) {
        const data = await res.json();
        if (data.status && data.data) {
          moduleAllCatCache.set(key, data.data);
          setArtworkCategories(data.data);
        }
      }
    } catch (e) {
      // Silent fail for categories
      setArtworkCategories([]);
    }
  }, []); // stable — reads model/size from refs


  // getCategoryId is only ever called via getCategoryIdRef.current (see artwork fetch
  // effect), so it does NOT need useCallback — a plain function stored in a ref is
  // cheaper because it never triggers downstream memoisation invalidations.
  const getCategoryIdRef = useRef<(name: string | null) => number | null>(null!);
  getCategoryIdRef.current = (categoryName: string | null): number | null => {
    if (!categoryName || categoryName === 'All') return null;
    const list: any[] = folderCategories.length ? folderCategories : artworkCategories;
    const found = list.find((c: any) => String(c?.name || '').toLowerCase() === String(categoryName).toLowerCase());
    const n = found?.id != null ? Number(found.id) : NaN;
    return Number.isFinite(n) && n > 0 ? n : null;
  };

  // ensure imageStatus resets when artworks array changes (e.g., pagination/filter)
  useEffect(() => {
    const newStatuses: Record<string, 'loading' | 'loaded' | 'error'> = {};
    artworks.forEach(a => {
      // If already loaded in this session, skip spinner
      if (loadedImagesRef.current.has(a.id)) {
        newStatuses[a.id] = 'loaded';
      } else {
        // preserve existing loaded/error if present, otherwise default to loading
        newStatuses[a.id] = imageStatus[a.id] || 'loading';
      }
    });
    setImageStatus(newStatuses);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artworks]);

  // Main function to add artwork to the design
  const addArtworkToDesign = useCallback((art: any, naturalWidth?: number, naturalHeight?: number, visibleBounds?: { left: number, top: number, right: number, bottom: number }) => {
    if (!selectedGranite || !updateDesign) return;

    updateDesign(prev => {
      const elements = prev.elements || [];
      // Only remove artwork from other granite types if we're switching granite types
      // Don't remove artwork if we're just adding new artwork to the same granite type
      const activeSlotType = activePlacementSlot?.slot_type;
      const isCornerActive = activeSlotType === 'corner_left' || activeSlotType === 'corner_right';
      // Ignore leftover opposite-color elements when deciding corner occupancy.
      const isCurrentGraniteCorner = (e: any, slot: 'corner_left' | 'corner_right') => {
        const placement = e?.properties?.slotType || e?.properties?.placement;
        if (placement !== slot) return false;
        const graniteType = e?.properties?.graniteType;
        return !graniteType || graniteType === selectedGranite;
      };

      const filteredElements = elements.filter(el => {
        // Keep all non-artwork elements
        if (el.type !== 'artwork') return true;

        // Keep template elements
        if (el.properties?.role === 'template' || el.properties?.isTemplate) return true;

        // Keep vase elements
        if (el.properties?.role === 'vase') return true;

        const elPlacement = String(el.properties?.slotType || el.properties?.placement || '').toLowerCase();
        const isPorcelainPhoto = el.properties?.role === 'porcelain' && !el.properties?.isPorcelainPlaceholder;
        const isElSpecial = elPlacement === 'special' || el.properties?.isPorcelainPlaceholder === true;
        const isIncomingSpecial = art.placement === 'special';

        // NEVER remove a user's real Porcelain photo when adding artwork
        if (isPorcelainPhoto) return true;

        if (isIncomingSpecial) {
          // Special design is separate: remove top, left, corner, and previous special placeholders
          if (elPlacement === 'top' || elPlacement === 'left' || elPlacement === 'corner' || elPlacement === 'corner_left' || elPlacement === 'corner_right' || isElSpecial) {
            return false;
          }
        } else {
          // Standard design (top, left, corner): remove special placeholders so they don't overlap, but keep real artwork and porcelain
          if (isElSpecial) {
            return false;
          }
        }

        return true; // Keep all top, left, corner, right artworks together (allows multiple artworks)
      });

      // Remove inner artworks whose parent was removed
      const removedParentIds = new Set(
        elements
          .filter(el => !filteredElements.includes(el))
          .map(el => el.id)
      );

      const removedParentArtCodes = new Set(
        elements
          .filter(el => !filteredElements.includes(el))
          .filter(el => el.properties?.artCode)
          .map(el => el.properties.artCode)
      );

      const finalFilteredElements = filteredElements.filter(el => {
        // Remove inner artwork if its parent was removed (by ID or by art code)
        if (el.properties?.isInnerArtwork) {
          if (el.properties?.parentId && removedParentIds.has(el.properties.parentId)) {
            return false;
          }
          if (el.properties?.parentArtCode && removedParentArtCodes.has(el.properties.parentArtCode)) {
            return false;
          }
        }
        return true;
      });

      // --- Determine which slots to fill ---
      let slotsToFill: any[] = [];
      // Custom uploads should always be placed at center, ignoring any active placement slot
      if (art.placement === 'special') {
        const specialSlot = placementSlots.find(s => s.slot_type === 'special' && s.placement_code === art.placement_code);
        if (specialSlot) slotsToFill.push(specialSlot);
        // NOTE: Do NOT auto-inject the porcelain slot here.
        // The porcelain element is created separately by the Porcelain panel when
        // the user uploads a photo. Injecting it here caused a duplicate/phantom
        // porcelain box to appear alongside the special design artwork.
      } else if (activePlacementSlot && !art.isCustomUpload) {
        if (isCornerActive) {
          const hasLeft = elements.some(e => isCurrentGraniteCorner(e, 'corner_left'));
          const hasRight = elements.some(e => isCurrentGraniteCorner(e, 'corner_right'));

          if (!hasLeft && !hasRight) {
            // Both empty: fill both with the same artwork
            slotsToFill = placementSlots.filter(s => s.slot_type === 'corner_left' || s.slot_type === 'corner_right');
          } else if (!hasLeft) {
            // Fill left
            slotsToFill = placementSlots.filter(s => s.slot_type === 'corner_left');
          } else if (!hasRight) {
            // Fill right
            slotsToFill = placementSlots.filter(s => s.slot_type === 'corner_right');
          } else {
            // Both full: replace the active one
            slotsToFill = [activePlacementSlot];
          }
        } else {
          // Standard slot
          slotsToFill = [activePlacementSlot];
        }
      } else if (!art.isCustomUpload && art.placement) {
        // No active slot selected, but artwork has a native placement (e.g. from Home search)
        // Try to find a matching slot on the stone automatically
        const artPlacement = String(art.placement).toLowerCase();
        const targetType = artPlacement === 'corner' ? 'corner_left' : artPlacement;
        let found = placementSlots.find(s => String(s.slot_type).toLowerCase() === targetType);

        if (!found && artPlacement === 'corner') {
          found = placementSlots.find(s => String(s.slot_type).toLowerCase() === 'corner_right');
        }

        if (found) {
          if (artPlacement === 'corner') {
            // For corners from Home search, we usually want to fill BOTH sides by default
            // unless the user has specifically selected one slot (which they haven't here)
            slotsToFill = placementSlots.filter(s => {
              const st = String(s.slot_type).toLowerCase();
              return st === 'corner_left' || st === 'corner_right';
            });

            // If for some reason filter returned nothing, fall back to the found slot
            if (slotsToFill.length === 0) slotsToFill = [found];
          } else {
            slotsToFill = [found];
          }
        } else {
          slotsToFill = [null];
        }
      } else {
        // No active slot and no native placement (or custom upload)
        slotsToFill = [null];
      }

      // Fallback: If logic resulted in no slots (e.g. special placement with no matching slot), ensure we at least add the artwork once
      if (slotsToFill.length === 0) {
        slotsToFill = [null];
      }

      const ppi = pixelsPerInch > 0 ? pixelsPerInch : 96;
      const smallSizePx = Math.floor(4 * ppi); // 4 inches
      const template = prev.elements.find(el => el.properties?.role === 'template' || el.properties?.isTemplate);

      const addedArtworks = slotsToFill.map((slot, sIdx) => {
        let curW = naturalWidth || art.width || 0;
        let curH = naturalHeight || art.height || 0;
        if (art.isCustomUpload && (!curW || !curH)) { curW = smallSizePx; curH = smallSizePx; }
        if (!curW || !curH) { curW = smallSizePx; curH = smallSizePx; }

        let ax: number = 0, ay: number = 0;
        const isSL12 = art.art_code === 'SL12';
        const dbFixedX = art.fixed_pos_x !== undefined && art.fixed_pos_x !== null ? Number(art.fixed_pos_x) : null;
        const dbFixedY = art.fixed_pos_y !== undefined && art.fixed_pos_y !== null ? Number(art.fixed_pos_y) : null;

        // 1. Placement Slot logic
        // Use slot logic if it's a generic placement, OR if it's a porcelain slot (even if SL12), OR if not SL12
        const isPorcelainSlot = slot?.slot_type === 'porcelain';

        if (slot && template) {
          const ox = renderedTemplateOrigin?.x ?? (template.x || 0);
          const oy = renderedTemplateOrigin?.y ?? (template.y || 0);
          const sx = ox + Number(slot.v1) * ppi;
          const sy = oy + Number(slot.h1) * ppi;
          const sw = (Number(slot.v2) - Number(slot.v1)) * ppi;
          const sh = (Number(slot.h2) - Number(slot.h1)) * ppi;

          if (isPorcelainSlot) {
            // Porcelain placeholder must EXACTLY match the slot dimensions/position
            curW = sw;
            curH = sh;
            ax = sx;
            ay = sy;
          } else if (visibleBounds) {
            const vW = Math.max(1, (visibleBounds.right - visibleBounds.left) * curW);
            const vH = Math.max(1, (visibleBounds.bottom - visibleBounds.top) * curH);
            const vRatio = vW / vH;
            const sRatio = sw / sh;
            const scaleFactor = (vRatio > sRatio) ? (sw / vW) : (sh / vH);
            curW = curW * scaleFactor;
            curH = curH * scaleFactor;
            const targetCenterX = sx + sw / 2;
            const targetCenterY = sy + sh / 2;
            const visualCenterX = (visibleBounds.left + visibleBounds.right) / 2 * curW;
            const visualCenterY = (visibleBounds.top + visibleBounds.bottom) / 2 * curH;
            ax = targetCenterX - visualCenterX;
            ay = targetCenterY - visualCenterY;
          } else {
            const artRatio = curW / curH;
            const slotRatio = sw / sh;
            if (artRatio > slotRatio) { curW = sw; curH = sw / artRatio; }
            else { curH = sh; curW = sh * artRatio; }
            ax = sx + (sw - curW) / 2;
            ay = sy + (sh - curH) / 2;
          }
        }
        // 2. Fixed DB position / SL12 (Skip for porcelain slots)
        else if (template && dbFixedY !== null && !isPorcelainSlot) {
          const ox = renderedTemplateOrigin?.x ?? template.x;
          const oy = renderedTemplateOrigin?.y ?? template.y;
          ax = dbFixedX !== null ? (ox + dbFixedX) : Math.floor(ox + (template.width - curW) / 2);
          ay = dbFixedY !== null ? (oy + dbFixedY) : Math.floor(oy + 10);
        }
        // 3. Layout Guides
        else if (layoutGuides) {
          const cx = (layoutGuides.left + layoutGuides.right) / 2;
          const cy = (layoutGuides.top + layoutGuides.bottom) / 2;
          ax = Math.floor(cx - curW / 2);
          ay = Math.floor(cy - curH / 2);
        }
        // 4. Default Stage Center
        else {
          ax = Math.floor(1200 / 2 - curW / 2);
          ay = Math.floor(600 / 2 - curH / 2);
        }

        // If an artwork already exists near (ax, ay), offset slightly so multiple artworks are easily selectable & draggable
        const existingNearCount = elements.filter(e => e.type === 'artwork' && Math.abs((e.x || 0) - ax) < 8 && Math.abs((e.y || 0) - ay) < 8).length;
        if (existingNearCount > 0 && !isPorcelainSlot && !isSL12) {
          ax += existingNearCount * 18;
          ay += existingNearCount * 18;
        }

        const artworkType = selectedGranite === 'PJB' ? 'Laser' : 'Sandblast';
        const uniqueIdSuffix = sIdx > 0 ? `-${sIdx}` : '';
        const isCornerRight = slot?.slot_type === 'corner_right' || art.placement === 'corner_right' || (!slot && art.placement === 'corner' && sIdx === 1);

        return {
          id: isPorcelainSlot ? `porcelain-${Date.now()}` : `art-${art.id}-${Date.now()}${uniqueIdSuffix}`,
          type: 'artwork' as const,
          side: activeSide || 'front',
          x: ax, y: ay, width: curW, height: curH, rotation: 0,
          flipX: isCornerRight ? true : false,
          flipY: false,
          properties: {
            src: isPorcelainSlot ? '' : resolveArtworkOriginalUrl(art, buildApiUrl),
            alt: isPorcelainSlot ? 'Porcelain Placeholder' : (art.title || art.art_code || 'Artwork'),
            code: isPorcelainSlot ? 'PORCELAIN' : art.art_code,
            artCode: isPorcelainSlot ? 'PORCELAIN' : art.art_code,
            artworkId: isPorcelainSlot ? null : (Number(art.id) || null),
            placement: slot ? slot.slot_type : art.placement,
            draggable: (dbFixedY !== null || slot?.slot_type === 'special' || isPorcelainSlot) ? false : true,
            isFixedPosition: (dbFixedY !== null || slot?.slot_type === 'special' || isPorcelainSlot),
            isSL12: isSL12,
            logicalWidth: naturalWidth || art.width,
            logicalHeight: naturalHeight || art.height,
            fixedPosX: dbFixedX,
            fixedPosY: dbFixedY,
            graniteType: selectedGranite,
            type: artworkType,
            role: isPorcelainSlot ? 'porcelain' : 'artwork',
            userResized: art.isCustomUpload,
            isCustomUpload: art.isCustomUpload || false,
            visibleBounds: visibleBounds,
            slotType: slot ? slot.slot_type : null,
            placementCode: art.placement_code
          }
        };
      });



      // Special handling for SL12 to ensure it's selected immediately
      if (art.art_code === 'SL12' && addedArtworks.length > 0) {
        setSelectedElement(addedArtworks[0].id);
      }

      return {
        ...prev,
        elements: [...finalFilteredElements, ...addedArtworks]
      };
    });
  }, [updateDesign, selectedGranite, layoutGuides, buildApiUrl, activeSide, setSelectedElement, renderedTemplateOrigin, placementSlots, activePlacementSlot]);

  const addInnerArtworkToDesign = useCallback((innerArt: any) => {
    if (!currentParentArt) return;

    updateDesign(prev => {
      // Find the parent element in current side
      const parentElement = prev.elements.find(el => el.properties?.artCode === currentParentArt.art_code && el.side === activeSide);
      if (!parentElement) {
        return prev;
      }

      // Remove existing inner artwork for this parent art code (not just by parent ID)
      // This handles cases where the parent was re-added with a new ID
      const filtered = prev.elements.filter(el => {
        if (!el.properties?.isInnerArtwork) return true;
        // Remove if it matches the parent ID OR the parent art code
        if (el.properties?.parentId === parentElement.id) return false;
        if (el.properties?.parentArtCode === currentParentArt.art_code && el.side === activeSide) return false;
        return true;
      });

      // Position from v1/v2/h1/h2 (raw inches), same logic as placement slot positioning.
      // v1 = horizontal start (inches), v2 = horizontal end (inches)
      // h1 = vertical start (inches),   h2 = vertical end (inches)
      const template = prev.elements.find(el => el.properties?.role === 'template' || el.properties?.isTemplate);
      const ox = renderedTemplateOrigin?.x ?? (template?.x || 0);
      const oy = renderedTemplateOrigin?.y ?? (template?.y || 0);
      const ppi = pixelsPerInch > 0 ? pixelsPerInch : 96;

      const x = ox + Number(innerArt.v1) * ppi;
      const y = oy + Number(innerArt.h1) * ppi;
      const width = (Number(innerArt.v2) - Number(innerArt.v1)) * ppi;
      const height = (Number(innerArt.h2) - Number(innerArt.h1)) * ppi;

      const parentIsCornerRight = parentElement.properties?.slotType === 'corner_right' || parentElement.properties?.placement === 'corner_right' || parentElement.flipX === true;

      const newInner = {
        id: `inner-art-${Date.now()}`,
        type: 'artwork' as const,
        side: activeSide || 'front',
        x, y, width, height, rotation: 0,
        flipX: parentIsCornerRight ? true : false,
        flipY: false,
        properties: {
          src: innerArt.url,
          alt: innerArt.title,
          isInnerArtwork: true,
          parentId: parentElement.id,
          parentArtCode: currentParentArt.art_code,
          draggable: false,
          fixedPosX: x - ox,
          fixedPosY: y - oy,
          role: 'artwork',
          type: selectedGranite === 'PJB' ? 'Laser' : 'Sandblast'
        }
      };

      // Splice the new inner artwork BEFORE its parent so it renders underneath it
      const parentIndex = filtered.findIndex((el: any) => el.id === parentElement.id);
      const newElements = [...filtered];
      if (parentIndex !== -1) {
        newElements.splice(parentIndex, 0, newInner);
      } else {
        newElements.push(newInner);
      }

      return {
        ...prev,
        elements: newElements
      };
    });
  }, [updateDesign, activeSide, selectedGranite, currentParentArt]);

  // Event Handlers
  const handleArtworkSelect = useCallback(async (art: any) => {
    if (!isTemplateSelected) return;

    // Load image to get natural dimensions and visual bounds if missing
    let naturalWidth = art.width;
    let naturalHeight = art.height;
    let visibleBounds = art.visibleBounds || null;

    if (!naturalWidth || !naturalHeight || !visibleBounds) {
      try {
        const url = resolveArtworkOriginalUrl(art, buildApiUrl);

        // Re-use already-loaded thumbnail image from memory cache to avoid a
        // redundant network request just for bounds detection.
        const cachedImg = imageMemoryCache.get(url);
        const imgForBounds: HTMLImageElement = cachedImg ?? await new Promise<HTMLImageElement>(async (resolve) => {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => resolve(img);
          img.onerror = () => resolve(img); // resolve anyway; dimensions will be 0
          let finalSrc = url;
          if (url.toLowerCase().includes('.svg')) {
            try {
              const res = await fetch(url, { mode: 'cors' });
              if (res.ok) {
                let svgText = await res.text();
                if (!/width=["']\d+/i.test(svgText)) {
                  const viewBoxMatch = svgText.match(/viewBox=["']\s*0\s+0\s+(\d+(?:\.\d+)?)\s+(\d+(?:\.\d+)?)/i);
                  if (viewBoxMatch) {
                    svgText = svgText.replace('<svg', `<svg width="${viewBoxMatch[1]}" height="${viewBoxMatch[2]}"`);
                  }
                }
                finalSrc = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgText)}`;
              }
            } catch (_) { }
          }
          img.src = finalSrc;
          setTimeout(() => resolve(img), 30000); // Increased timeout for high-res assets
        });

        naturalWidth = imgForBounds.naturalWidth || naturalWidth;
        naturalHeight = imgForBounds.naturalHeight || naturalHeight;

        // The image is loaded (or cached) with crossOrigin='anonymous' and the
        // AssetController sends Access-Control-Allow-Origin:*, so getImageData
        // is safe.  This detects tight visual bounds, stripping transparent padding.
        try {
          visibleBounds = await computeVisualBounds(imgForBounds);
        } catch {
          // If bounds detection fails for any reason, fall back to full image rect
          visibleBounds = null;
        }
      } catch (e) {
        // silent — proceed without bounds
      }
    }

    setSelectingArt(true);
    try {
      if (art.art_code === 'SL12') {
        addArtworkToDesign(art, naturalWidth, naturalHeight, visibleBounds);
        setCurrentParentArt(art);
        setShowInnerSelection(true);
        fetchInnerArtworks(art.art_code);
        return; // Do not close panel
      }

      if (art.placement === 'special') {
        const porcelainSlot = placementSlots.find((s: any) => s.slot_type === 'porcelain' && s.placement_code === art.placement_code);
        const specialSlot = placementSlots.find((s: any) => s.slot_type === 'special' && s.placement_code === art.placement_code);
        if (porcelainSlot) setActivePlacementSlot(porcelainSlot);
        else if (specialSlot) setActivePlacementSlot(specialSlot);
      } else if (!activePlacementSlot && art.placement) {
        // When selecting from Home search, automatically set the slot so the UI stays in sync
        const artPlacement = String(art.placement).toLowerCase();
        const targetType = artPlacement === 'corner' ? 'corner_left' : artPlacement;
        let found = placementSlots.find(s => String(s.slot_type).toLowerCase() === targetType);
        if (!found && artPlacement === 'corner') found = placementSlots.find(s => String(s.slot_type).toLowerCase() === 'corner_right');
        if (found) setActivePlacementSlot(found);
      }

      try {
        addArtworkToDesign(art, naturalWidth, naturalHeight, visibleBounds);
      } catch (err) {
      }
      onArtworkSelect(art);
      onClose();
    } finally {
      setSelectingArt(false);
    }
  }, [isTemplateSelected, selectedGranite, addArtworkToDesign, onArtworkSelect, onClose, fetchInnerArtworks, buildApiUrl, placementSlots, setActivePlacementSlot]);

  // Handle custom artwork upload (temporary, not stored in database)
  const handleCustomArtworkUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (setActivePlacementSlot) setActivePlacementSlot(null);
    const file = event.target.files && event.target.files[0];
    if (!file || !selectedGranite || !isTemplateSelected) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      let dataUrl = e.target?.result as string;
      if (!dataUrl) return;

      // Automatically convert to grayscale for non-Porcelain uploads
      // In this context, everything uploaded via Art.tsx is treated as Artwork/Laser
      try {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = dataUrl;
        });

        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          // Keep the canvas background fully transparent (default) so that PNG
          // images with transparent areas stay transparent after grayscale conversion.
          // Only fill a white background for non-PNG files (JPEG has no alpha channel).
          const isPng = file.type === 'image/png' || dataUrl.startsWith('data:image/png');
          if (!isPng) {
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
          }
          ctx.filter = 'grayscale(100%)';
          ctx.drawImage(img, 0, 0);
          // Use PNG for images that may have transparency to avoid black fill on
          // transparent areas; use JPEG for photos (smaller file size).
          dataUrl = isPng
            ? canvas.toDataURL('image/png')
            : canvas.toDataURL('image/jpeg', 0.95);
        }

        // Calculate aspect ratio preserved small size
        const ppi = Number((window as any)?.__appPixelsPerInch || 96);
        const targetSize = 4 * ppi; // 4 inches
        const ratio = img.width / img.height;

        let initialW, initialH;
        if (ratio > 1) {
          initialW = targetSize;
          initialH = targetSize / ratio;
        } else {
          initialH = targetSize;
          initialW = targetSize * ratio;
        }

        // Create a custom artwork element
        const customArt = {
          id: `custom-${Date.now()}`,
          art_code: 'CUSTOM',
          title: 'Custom Artwork',
          placement: 'custom',
          thumb_url: dataUrl,
          image_url: dataUrl,
          src: dataUrl,
          width: Math.floor(initialW),
          height: Math.floor(initialH),
          isCustomUpload: true // Flag to identify custom uploads
        };

        // Add to design like regular artwork
        try {
          addArtworkToDesign(customArt);
          onArtworkSelect(customArt);
          // Reset navigation session so reopening the modal starts fresh at Home
          // (placement, category, artworks). If we don't do this, the session may
          // restore a half-loaded state (e.g. placement set but artworks empty)
          // which causes the modal to display a blank white area with only a Back button.
          moduleSession.placement = null;
          moduleSession.category = null;
          moduleSession.artworks = [];
          onClose();
        } catch (err) {
        }
      } catch (err) {
      }

      // Reset input
      if (customArtworkInputRef.current) {
        customArtworkInputRef.current.value = '';
      }
    };
    reader.readAsDataURL(file);
  }, [selectedGranite, isTemplateSelected, addArtworkToDesign, onArtworkSelect, onClose]);


  const handleResetFilters = useCallback(() => {
    setSelectedPlacement(null);
    setSelectedCategory(null);
    setArtQuery('');
    setArtPage(1);
    setArtworks([]);
    // Clear active placement slot so the sync-effect doesn't immediately
    // re-set selectedPlacement back after we reset it (which was the bug
    // causing Home breadcrumb navigation to have no effect).
    if (setActivePlacementSlot) setActivePlacementSlot(null);
    // Reset dedup keys so next fetch goes through
    lastArtworkFetchKeyRef.current = '__INIT__';
    lastPlacementFetchKeyRef.current = '__INIT__';
  }, [setActivePlacementSlot]);

  const handleBack = useCallback(() => {
    // Navigate one step up: Inner selection -> artwork grid -> category -> placement -> granite
    if (showInnerSelection) {
      setShowInnerSelection(false);
      setCurrentParentArt(null);
      setUserDismissedInnerSelection(true); // Prevent auto-reopening
      return;
    }

    // Artwork grid → Category step
    // NOTE: do NOT clear artworks — keeping them in state means the <img>
    // elements stay in the DOM (hidden by StepContainer display:none) and
    // the browser never needs to re-request the same images.
    // Exception: for 'special' placement the category step is auto-skipped,
    // so Back goes directly to the placement step.
    if (selectedCategory) {
      if (selectedPlacement === 'special') {
        // Skip the (auto-set) category step — go straight back to placement selection
        setSelectedCategory(null);
        setSelectedPlacement(null);
        setArtQuery('');
        setDebouncedArtQuery('');
        lastArtworkFetchKeyRef.current = '__INIT__';
        if (setActivePlacementSlot) setActivePlacementSlot(null);
        setArtworks([]);
        setArtPage(1);
        return;
      }
      setSelectedCategory(null);
      setArtPage(1);
      return;
    }


    // Category step → Placement step
    // Clear artworks + reset dedup so re-selecting this placement re-fetches.
    if (selectedPlacement) {
      setSelectedPlacement(null);
      lastArtworkFetchKeyRef.current = '__INIT__';
      if (setActivePlacementSlot) setActivePlacementSlot(null);
      setArtworks([]);
      setArtPage(1);
      return;
    }

    // In template mode, granite is pre-set so there is no granite step.
    // Back from placement list means there's nowhere to go — close the panel.
    if (!isLibraryMode) {
      onClose();
      return;
    }

    // Library mode: go back to granite selection
    if (selectedGranite) {
      setSelectedGranite(null);
      handleResetFilters();
      setArtworks([]);
      return;
    }

    // Fallback: if nothing to go back to, close the panel
    onClose();
  }, [showInnerSelection, selectedCategory, selectedPlacement, selectedGranite, isLibraryMode, onClose, setActivePlacementSlot, handleResetFilters]);

  const handleGraniteChange = useCallback((granite: string) => {
    setSelectedGranite(granite);
    handleResetFilters();
    setArtworks([]);
    lastCategoryFetchKeyRef.current = '__INIT__';
    lastPlacementFetchKeyRef.current = '__INIT__';
    lastArtworkFetchKeyRef.current = '__INIT__';
  }, [handleResetFilters]);

  const handlePlacementChange = useCallback((placement: string) => {
    setSelectedPlacement(placement);
    setSelectedCategory(null); // always show category step first
    // Reset artwork dedup so the placement fetch always runs (serves from module cache if visited before)
    lastArtworkFetchKeyRef.current = '__INIT__';
    setArtPage(1);
    setArtworks([]);

    // Sync back to designer outline
    // For 'special' placement, don't set activePlacementSlot yet - wait for specific artwork selection
    if (placement !== 'special' && placementSlots && placementSlots.length > 0) {
      const targetType = (placement === 'corner') ? 'corner_left' : placement;
      let found = placementSlots.find(s => s.slot_type === targetType);

      // If corner_left not found for 'corner', try corner_right
      if (!found && placement === 'corner') {
        found = placementSlots.find(s => s.slot_type === 'corner_right');
      }

      if (found) {
        setActivePlacementSlot(found);
      } else {
        setActivePlacementSlot(null);
      }
    } else if (placement === 'special') {
      // Clear any existing placement slot when selecting special category
      setActivePlacementSlot(null);
    }
  }, [placementSlots, setActivePlacementSlot, updateDesign]);

  // Sync active placement slot from designer click while the panel is already open
  useEffect(() => {
    if (activePlacementSlot) {
      const pType = activePlacementSlot.slot_type;
      let targetPlacement = pType;
      if (pType === 'corner_left' || pType === 'corner_right') targetPlacement = 'corner';
      else if (pType === 'porcelain') targetPlacement = 'special';

      if (targetPlacement !== selectedPlacement) {
        setSelectedPlacement(targetPlacement);
        setSelectedCategory(null);
        lastArtworkFetchKeyRef.current = '__INIT__';
        setArtPage(1);
        setArtworks([]);
      }
    }
  }, [activePlacementSlot, selectedPlacement]);

  // Effects
  useEffect(() => {
    if (sharedCategories.length === 0 || forcedGranite) {
      fetchAllCategories(forcedGranite ?? undefined);
    }
  }, [fetchAllCategories, sharedCategories.length, forcedGranite]);

  useEffect(() => {
    if (!isLibraryMode && selectedGranite) {
      fetchPlacements(selectedGranite);
    }
  }, [selectedGranite, fetchPlacements, isLibraryMode]);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedArtQuery(artQuery);
    }, 400); // 400ms debounce
    return () => clearTimeout(timer);
  }, [artQuery]);

  // Special placement skips the category step — go straight to the image grid.
  // When selectedPlacement becomes 'special' and no category is chosen yet:
  //   1. Auto-set category to 'All' so the artwork grid StepContainer becomes visible.
  //   2. Reset the fetch dedup key and directly invoke fetchArtworks so the images
  //      always load — even on a 2nd+ visit after Back — without relying on the
  //      effect chain (which can skip the fetch if the key matches a stale value).
  useEffect(() => {
    if (selectedPlacement === 'special' && !selectedCategory && selectedGranite) {
      setSelectedCategory('All');
      // Force-fetch special artworks regardless of dedup cache state
      const filters: any = { granite_type: selectedGranite, placement: 'special' };
      if (selectedModelId) filters.model_id = selectedModelId;
      if (selectedSizeId) filters.size_id = selectedSizeId;
      lastArtworkFetchKeyRef.current = JSON.stringify(filters); // mark as fetched
      fetchArtworks(filters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlacement, selectedCategory]);


  useEffect(() => {
    if (!selectedGranite) return;

    // Template mode: fetch the full placement set as soon as a placement is chosen OR if searching from Home.
    // folderCategories are then derived client-side from the returned artworks.
    // Library mode: wait until category is chosen (pool may be huge).
    if (!isLibraryMode && !selectedPlacement && !debouncedArtQuery) return;
    if (isLibraryMode && !selectedCategory) return;

    const filters: any = { granite_type: selectedGranite };
    if (selectedPlacement) filters.placement = selectedPlacement;
    if (selectedModelId) filters.model_id = selectedModelId;
    if (selectedSizeId) filters.size_id = selectedSizeId;
    if (debouncedArtQuery) filters.search = debouncedArtQuery;

    // Library mode only: apply server-side category filter
    if (isLibraryMode && selectedCategory && selectedCategory !== 'All') {
      const catId = getCategoryIdRef.current(selectedCategory);
      if (catId) filters.category_id = catId;
    }

    const key = JSON.stringify(filters);
    if (lastArtworkFetchKeyRef.current === key) return; // skip duplicate
    lastArtworkFetchKeyRef.current = key;

    fetchArtworks(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGranite, selectedPlacement, selectedCategory, debouncedArtQuery, fetchArtworks, selectedModelId, selectedSizeId, isLibraryMode]);



  useEffect(() => {
    if (setImageGridActive) {
      const active = isLibraryMode
        ? !!selectedGranite
        : (!!selectedGranite && !!selectedPlacement && !!selectedCategory);
      setImageGridActive(active);
    }
  }, [selectedGranite, selectedPlacement, selectedCategory, setImageGridActive, isLibraryMode]);

  // Keep moduleSession in sync so close/reopen restores navigation state
  useEffect(() => {
    moduleSession.granite = selectedGranite;
    moduleSession.modelId = selectedModelId;
    moduleSession.sizeId = selectedSizeId;
    moduleSession.placement = selectedPlacement;
    moduleSession.category = selectedCategory;
    moduleSession.artworks = artworks;
  }, [selectedGranite, selectedModelId, selectedSizeId, selectedPlacement, selectedCategory, artworks]);

  useEffect(() => {
    // Skip on initial mount — selectedGranite is already initialised from the prop.
    // Running this on mount resets every dedup key that the other mount effects
    // just set, which is the primary cause of duplicate API requests.
    if (forcedGraniteInitRef.current) {
      forcedGraniteInitRef.current = false;
      return;
    }
    // Only reset when forcedGranite is actually DIFFERENT from the current granite.
    // If it merely changes from null → 'PJB' while selectedGranite is already 'PJB'
    // (e.g. the LeftPanel color effect fires after the Art panel has already opened),
    // there is nothing to reset — calling handleResetFilters() here would clear the
    // selectedCategory, corrupt moduleSession, and show the category page on reopen.
    if (forcedGranite !== undefined && forcedGranite !== selectedGraniteRef.current) {
      setSelectedGranite(forcedGranite);
      handleResetFilters();
      setArtworks([]);
      // Do NOT reset lastCategoryFetchKeyRef here — the category key already
      // embeds granite_type so it changes naturally when granite changes.
      // Resetting it caused a duplicate categories fetch on every mount.
      lastPlacementFetchKeyRef.current = '__INIT__';
      lastArtworkFetchKeyRef.current = '__INIT__';
    }
  }, [forcedGranite, handleResetFilters]);

  // Breadcrumbs logic
  const breadcrumbs: BreadcrumbItem[] = useMemo(() => {
    const crumbs: BreadcrumbItem[] = [
      {
        label: 'Home',
        onClick: () => {
          // In template mode, "Home" resets to the placement step.
          // In library mode, it resets fully (back to granite selection).
          if (!isLibraryMode) {
            handleResetFilters();
          } else {
            setSelectedGranite(forcedGranite ?? null);
            handleResetFilters();
          }
        },
        isCurrent: isLibraryMode ? !selectedGranite : !selectedPlacement
      }
    ];

    // Only show granite crumb in library mode — template mode starts at placement.
    if (isLibraryMode && selectedGranite) {
      crumbs.push({
        label: getGraniteLabel(selectedGranite),
        onClick: () => {
          setSelectedGranite(selectedGranite);
          handleResetFilters();
        },
        isCurrent: !selectedPlacement
      });
    }

    if (selectedGranite && selectedPlacement) {
      crumbs.push({
        label: getPlacementLabel(selectedPlacement),
        onClick: () => {
          setSelectedPlacement(selectedPlacement);
          setSelectedCategory(null);
          setArtPage(1);
          setShowInnerSelection(false);
        },
        // Special placement auto-sets category='All' so treat the artwork grid as the current step here
        isCurrent: !!selectedPlacement && (!selectedCategory || selectedPlacement === 'special') && !showInnerSelection
      });
    }

    // Don't show the 'All' category crumb for special placement — the category step is auto-skipped
    if (selectedGranite && selectedPlacement && selectedCategory && selectedPlacement !== 'special') {
      crumbs.push({
        label: selectedCategory,
        onClick: showInnerSelection ? () => { setShowInnerSelection(false); setCurrentParentArt(null); } : undefined,
        isCurrent: !showInnerSelection
      });
    }

    if (selectedGranite && selectedPlacement && selectedCategory && showInnerSelection) {
      crumbs.push({
        label: `${currentParentArt?.art_code || 'Inner'} Options`,
        isCurrent: true
      });
    }

    return crumbs;
  }, [
    selectedGranite,
    selectedPlacement,
    selectedCategory,
    showInnerSelection,
    isLibraryMode,
    forcedGranite,
    handleResetFilters,
    currentParentArt
  ]);

  // Render helpers
  const renderInnerSelection = () => (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <SectionTitle>Select Options for {currentParentArt?.art_code || 'Artwork'}</SectionTitle>
      <div style={{ flex: 1, overflowY: 'auto', scrollbarGutter: 'stable' }}>
        <ArtGrid style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))' }}>
          {innerArtworks.map((img: any) => (
            <ArtCard
              key={img.id}
              onClick={() => addInnerArtworkToDesign(img)}
              title={img.title}
              style={{ height: 'fit-content' }}
            >
              <Thumb style={{ aspectRatio: '1/1' }}>
                <img src={img.url} alt={img.title} />
              </Thumb>
              <Caption>{img.title}</Caption>
            </ArtCard>
          ))}
          {innerLoading && (
            <div style={{ gridColumn: '1/-1', padding: 20, textAlign: 'center' }}>

              <div style={{ marginTop: 8, fontSize: 12, color: '#64748b' }}>Loading options...</div>
            </div>
          )}
        </ArtGrid>
      </div>
      <div style={{ display: 'flex', marginTop: 12, padding: '10px 0', borderTop: '1px solid #f1f5f9' }}>
        <Button
          $variant="primary"
          onClick={() => { setShowInnerSelection(false); setCurrentParentArt(null); onClose(); }}
          style={{ flex: 1 }}
        >
          Done
        </Button>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
        <Button onClick={handleBack} style={{ fontSize: 10, padding: '4px 8px', background: '#fff', color: '#666', border: '1px solid #ddd' }}>Back</Button>
      </div>
    </div>
  );
  const renderGraniteSelection = () => (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 13 }}>Granite Color</div>
      <div style={{ display: 'flex', gap: 8 }}>
        {forcedGranite ? (
          <Button
            style={{
              border: '2px solid #3498db',
              background: '#eaf6fd',
              cursor: 'not-allowed'
            }}
            disabled
          >
            {getGraniteLabel(forcedGranite)}
          </Button>
        ) : (
          GRANITES.map(granite => (
            <Button
              key={granite.id}
              onClick={() => handleGraniteChange(granite.id)}
            >
              {granite.label}
            </Button>
          ))
        )}
      </div>
    </div>
  );

  const renderPlacementSelection = () => (
    <div style={{ marginBottom: 10 }}>
      {artQuery.trim() ? (
        renderArtworkGrid()
      ) : (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
            <div style={{ fontWeight: 600, fontSize: 14, textTransform: "uppercase", color: '#34495e' }}>Design Placement</div>
            {selectedGranite === 'PJB' && (
              <Button
                $variant="primary"
                onClick={() => customArtworkInputRef.current?.click()}
                disabled={!isTemplateSelected}
                title="Upload custom artwork"
                style={{ whiteSpace: 'nowrap', fontSize: 11 }}
              >
                Upload your photo or artwork
              </Button>
            )}
          </div>
        </>
      )}
      {!artQuery.trim() && (
        filteredPlacements.length > 0 ? (
          <PlacementGrid>
            {filteredPlacements.map(placement => (
              <PlacementCard key={placement.id} onClick={() => handlePlacementChange(placement.id)}>
                {placement.label}
              </PlacementCard>
            ))}
          </PlacementGrid>
        ) : (
          !placementsLoading && (
            <div style={{ color: '#64748b', fontSize: 13, marginTop: 12, marginBottom: 12, textAlign: 'center', background: '#f8fafc', padding: 16, borderRadius: 8, border: '1px dashed #e2e8f0' }}>
              No placements available for {getGraniteLabel(selectedGranite)}
            </div>
          )
        )
      )}
    </div>
  );

  const renderCategorySelection = () => {
    // folderCategories is derived from artworks — if empty here it means
    // no artworks were found for this placement (not a loading state).
    if (folderCategories.length === 0) {
      return (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <SectionTitle style={{ marginBottom: 0 }}>Artwork Category</SectionTitle>
            {selectedGranite === 'PJB' && (
              <Button
                $variant="primary"
                onClick={() => customArtworkInputRef.current?.click()}
                disabled={!isTemplateSelected}
                title="Upload custom artwork"
                style={{ whiteSpace: 'nowrap', fontSize: 11 }}
              >
                Upload your photo or artwork
              </Button>
            )}
          </div>
          <div style={{ color: '#e74c3c', fontSize: 10, marginBottom: 10 }}>
            No categories found for this placement
          </div>
          {/* Navigation handled by footer (Cancel / Back) */}
        </>
      );
    }

    return (
      <>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <SectionTitle style={{ marginBottom: 0 }}>Artwork Category</SectionTitle>
        </div>

        {artQuery.trim() ? (
          renderArtworkGrid()
        ) : (
          <FolderGrid>
            <FolderCard key="all" onClick={() => setSelectedCategory('All')}>
              <FolderLabel>All</FolderLabel>
              {(() => {
                const total = Array.isArray(folderCategories)
                  ? folderCategories.reduce((sum: number, c: any) => sum + (Number(c?.artwork_count) || 0), 0)
                  : 0;
                return total ? <FolderMeta>{`${total} design${total === 1 ? '' : 's'}`}</FolderMeta> : null;
              })()}
            </FolderCard>

            {folderCategories.map(category => (
              <FolderCard key={category.id} onClick={() => setSelectedCategory(category.name)}>
                <FolderLabel>{String(category.name || '')}</FolderLabel>
                {(() => {
                  const count = Number(category.artwork_count);
                  if (!Number.isFinite(count) || count <= 0) return null;
                  return <FolderMeta>{`${count} design${count === 1 ? '' : 's'}`}</FolderMeta>;
                })()}
              </FolderCard>
            ))}
          </FolderGrid>
        )}
        {/* Navigation handled by footer (Cancel / Back) */}
      </>
    );
  };

  const renderArtworkGrid = () => (
    <>
      {(() => {
        const count = Array.isArray(filteredArtworks) ? filteredArtworks.length : 0;
        return (
          <div style={{ fontSize: 10, color: '#666', marginBottom: 8 }}>
            Showing {selectedGranite === 'PJB' ? 'Laser' : 'Sandblast'} designs
            {selectedPlacement ? ` for ${selectedPlacement}` : ''}
            {selectedCategory ? ` in ${selectedCategory}` : ''}
            {count > 0 && (
              <span>{` (${count} found)`}</span>
            )}
          </div>
        );
      })()}

      {artError && (
        <div style={{ color: '#e74c3c', fontSize: 10, textAlign: 'center', marginBottom: 10 }}>
          Error: {artError}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <ArtGrid $columns={selectedPlacement === 'top' || selectedPlacement === 'special' ? '1fr' : undefined}>
          {/* Render ALL artworks; only CSS-hide those outside the current page.
              This keeps every <img> in the DOM so switching categories/pages
              never causes remounts and therefore never fires extra requests. */}
          {artworks.map((artwork: any) => {
            const isVisible = pageItemIdSet.has(artwork.id);
            const { primary } = resolveArtworkThumbUrls(artwork, buildApiUrl);
            const status = imageStatus[artwork.id] || (primary ? 'loading' : 'error');

            return (
              <div key={artwork.id} style={{ display: isVisible ? undefined : 'none', minWidth: 0 }}>
                <ArtItem
                  artwork={artwork}
                  status={status}
                  primary={primary}
                  isTemplateSelected={isTemplateSelected}
                  selectedPlacement={selectedPlacement}
                  graniteColor={selectedGranite}
                  onSelect={handleArtworkSelect}
                  onLoad={(id) => {
                    loadedImagesRef.current.add(id);
                    setImageStatus(prev => ({ ...prev, [id]: 'loaded' }));
                  }}
                  onError={(id) => {
                    setImageStatus(prev => ({ ...prev, [id]: 'error' }));
                  }}
                />
              </div>
            );
          })}
          {!artLoading && !pageItems.length && (
            <div style={{ gridColumn: '1/-1', fontSize: 10, color: '#b0b7c3', textAlign: 'center' }}>
              No artworks found matching your criteria
            </div>
          )}
        </ArtGrid>
      </div>

      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 6, gap: 6 }}>
          <Button
            disabled={safePage <= 1}
            onClick={() => setArtPage(safePage - 1)}
            style={{ padding: '2px 7px', fontSize: 10 }}
          >
            Prev
          </Button>
          <span style={{ fontSize: 10 }}>Page {safePage} / {totalPages}</span>
          <Button
            disabled={safePage >= totalPages}
            onClick={() => setArtPage(safePage + 1)}
            style={{ padding: '2px 7px', fontSize: 10 }}
          >
            Next
          </Button>
        </div>
      )}
    </>
  );

  return (
    <div
      style={{
        fontFamily: "'Inter', 'Segoe UI', Arial, sans-serif",
        fontSize: 12,
        borderRadius: 8,
        position: 'relative',
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        minWidth: 0
      }}
    >
      {/* Breadcrumb navigation (hidden while any preloader is active) */}
      <Breadcrumbs>
        {breadcrumbs.map((crumb, index) => (
          <React.Fragment key={index}>
            {index > 0 && <span className="sep">{'>'}</span>}
            {crumb.isCurrent ? (
              <span className="current">{crumb.label}</span>
            ) : (
              <span className="crumb" onClick={crumb.onClick}>{crumb.label}</span>
            )}
          </React.Fragment>
        ))}
      </Breadcrumbs>

      {/* Content area — flex:1 + overflow:auto keeps this scrollable while footer stays pinned */}
      <ScrollableContent>
        {/* Preloader overlay — placements */}
        {isPreloading && (
          <div style={{
            position: 'absolute',
            inset: 0,
            background: 'rgba(255, 255, 255, 0.7)',
            zIndex: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <CenterLoader style={{ padding: 0 }}>
              <LocalSpinner $size={32} />
              <span>Loading...</span>
            </CenterLoader>
          </div>
        )}

        <FormGroup style={{ flex: 1, minHeight: 0 }}>
          {/* SL12 Inner selection view */}
          {showInnerSelection && (
            renderInnerSelection()
          )}

          <div style={{ display: showInnerSelection ? 'none' : 'flex', height: '100%', flexDirection: 'column', minHeight: 0 }}>
            {/* Granite selection */}
            <StepContainer $isVisible={!selectedGranite}>
              {renderGraniteSelection()}
            </StepContainer>

            {/* Placement selection */}
            <StepContainer $isVisible={!isLibraryMode && !!selectedGranite && !selectedPlacement && !placementsLoading}>
              <div style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ position: 'relative', flex: 1 }}>
                    <span style={{ position: 'absolute', left: 8, top: 6, fontSize: 12, color: '#b0b7c3' }}>🔍</span>
                    <Input
                      value={artQuery}
                      onChange={(e) => {
                        const val = e.target.value;
                        setArtPage(1);
                        setArtQuery(val);
                      }}
                      placeholder="Search items by name or code..."
                    />
                  </div>
                </div>
              </div>
              {renderPlacementSelection()}
            </StepContainer>

            {/* Category selection Step 2 */}
            <StepContainer $isVisible={!isLibraryMode && !!selectedGranite && !!selectedPlacement && !selectedCategory}>
              {!artQuery.trim() && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ position: 'relative', flex: 1 }}>
                      <span style={{ position: 'absolute', left: 8, top: 6, fontSize: 12, color: '#b0b7c3' }}>🔍</span>
                      <Input
                        value={artQuery}
                        onChange={(e) => {
                          const val = e.target.value;
                          setArtPage(1);
                          setArtQuery(val);
                        }}
                        placeholder="Search items by name or code..."
                      />
                    </div>

                    {selectedGranite === 'PJB' && (
                      <Button
                        $variant="primary"
                        onClick={() => customArtworkInputRef.current?.click()}
                        disabled={!isTemplateSelected}
                        title="Upload custom artwork"
                        style={{ whiteSpace: 'nowrap', fontSize: 11 }}
                      >
                        Upload Photo
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {artworks.length > 0 ? (
                renderCategorySelection()
              ) : !artLoading && artworks.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#64748b', fontSize: 13, padding: '24px 0' }}>
                  No designs found for this placement
                </div>
              ) : null}
            </StepContainer>

            {/* Artwork grid step (Step 3) */}
            <StepContainer $isVisible={!!selectedGranite && (isLibraryMode || (!!selectedPlacement && !!selectedCategory))}>
              {/* Only show search bar at the top level of the grid if in LibraryMode (no steps)
                  OR if Step 3 (category selected) */}
              {(isLibraryMode || (!!selectedPlacement && !!selectedCategory)) && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ position: 'relative', flex: 1 }}>
                      <span style={{ position: 'absolute', left: 8, top: 6, fontSize: 12, color: '#b0b7c3' }}>🔍</span>
                      <Input
                        value={artQuery}
                        onChange={(e) => {
                          const val = e.target.value;
                          setArtPage(1);
                          setArtQuery(val);
                        }}
                        placeholder="Search items by name or code..."
                      />
                    </div>

                    {selectedGranite === 'PJB' && (
                      <Button
                        $variant="primary"
                        onClick={() => customArtworkInputRef.current?.click()}
                        disabled={!isTemplateSelected}
                        title="Upload custom artwork"
                        style={{ whiteSpace: 'nowrap', fontSize: 11 }}
                      >
                        Upload Photo
                      </Button>
                    )}
                  </div>
                </div>
              )}
              {renderArtworkGrid()}
            </StepContainer>
          </div>
        </FormGroup>
      </ScrollableContent>

      {/* Footer — always pinned to the bottom of the panel */}
      <div style={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: 10,
        padding: '10px 16px',
        borderTop: '1px solid #f1f5f9',
        background: '#fff',
        flexShrink: 0,
      }}>
        {!showInnerSelection && (selectedPlacement || selectedCategory) ? (
          <Button onClick={handleBack}>Back</Button>
        ) : (
          !isPreloading && !showInnerSelection ? (
            <Button onClick={onClose}>Cancel</Button>
          ) : null
        )}
      </div>

      <input
        ref={customArtworkInputRef}
        type="file"
        accept="image/*"
        onChange={handleCustomArtworkUpload}
        style={{ display: 'none' }}
      />
    </div>
  );
};

export default Art;